import { computed, ref, type ComputedRef, type Ref, type WritableComputedRef } from 'vue'
import FileName from '@/model/ComControl/FileName'

type SettingsImageOptionFileDraft = {
  type: number
  name: string
}

export type SettingsImageOptionDraft = {
  Name: string
  NoneImage: SettingsImageOptionFileDraft
  MoveImage: SettingsImageOptionFileDraft
  X: number
  Y: number
}

type SettingsIndexedPosition = {
  Index: number
  X: number
  Y: number
}

type SettingsButtonAssignment = {
  enabled: boolean
  index: number
}

type SettingsButtonGroupDraft = {
  Buttons: SettingsButtonAssignment[]
}

type SettingsReplayDraftLike = {
  ButtonIndex: number
  CloseEx: SettingsIndexedPosition
}

type SettingsSaveLoadDraftLike = {
  BackButtonEx: SettingsIndexedPosition
  CloseButtonEx: SettingsIndexedPosition
  PrePic: SettingsIndexedPosition
  NextPic: SettingsIndexedPosition
}

export type SettingsImageOptionPickerItem = {
  index: number
  label: string
  name: string
  noneImagePath: string
  moveImagePath: string
}

type UseSettingsImageOptionsParams = {
  imageOptionGraphicBaseFolderPath: string
  resolveGraphicPreviewUrl: (storedPath: string, baseFolderPath?: string) => string
  titleSetting: Ref<SettingsButtonGroupDraft | null>
  menuSetting: Ref<SettingsButtonGroupDraft | null>
  systemSetting: Ref<SettingsButtonGroupDraft | null>
  replaySetting: Ref<SettingsReplayDraftLike | null>
  saveLoadSetting: Ref<SettingsSaveLoadDraftLike | null>
  applyReplayButtonIndex?: (index: number) => void
}

/** 统一暴露设置图片选项页需要消费的响应式状态与动作。 */
type UseSettingsImageOptionsResult = {
  imageOptionDraft: Ref<SettingsImageOptionDraft[]>
  imageOptionButtons: ComputedRef<SettingsImageOptionDraft[]>
  imageOptionMaxCount: WritableComputedRef<number>
  imageOptionSelectedIndex: Ref<number>
  imageOptionHover: Ref<boolean>
  imageOptionPickerItems: ComputedRef<SettingsImageOptionPickerItem[]>
  isReservedImageOption: ComputedRef<boolean>
  selectedImageOption: ComputedRef<SettingsImageOptionDraft | null>
  selectedImageOptionNoneUrl: ComputedRef<string>
  selectedImageOptionMoveUrl: ComputedRef<string>
  imageOptionTestUrl: ComputedRef<string>
  showImageOptionButtonTest: ComputedRef<boolean>
  setImageOptionButtons: (buttons: SettingsImageOptionDraft[]) => void
  resetImageOptions: () => void
  selectImageOption: (index: number) => void
  formatImageOptionLabel: (index: number, item: SettingsImageOptionDraft) => string
  updateSelectedImageOptionNonePic: (value: string) => void
  updateSelectedImageOptionMovePic: (value: string) => void
  resolveImageOptionPreviewUrl: (index: number) => string
}

const RESERVED_IMAGE_OPTION_NAME = '[系统保留]未使用'

function createEmptyFileDraft(): SettingsImageOptionFileDraft {
  return {
    type: Number(FileName.DEF_TYPE),
    name: '',
  }
}

function createEmptyImageOptionButton(): SettingsImageOptionDraft {
  return {
    Name: '',
    NoneImage: createEmptyFileDraft(),
    MoveImage: createEmptyFileDraft(),
    X: 0,
    Y: 0,
  }
}

/**
 * 创建图片选项保留位草稿。
 * @description 保留位始终占用索引 0，用于兼容工程里依赖“未使用按钮”的引用。
 */
export function createReservedSettingsImageOptionButton(): SettingsImageOptionDraft {
  return {
    ...createEmptyImageOptionButton(),
    Name: RESERVED_IMAGE_OPTION_NAME,
  }
}

function normalizeImageOptionButtons(source: SettingsImageOptionDraft[]): SettingsImageOptionDraft[] {
  const next = source.length ? source : [createReservedSettingsImageOptionButton()]
  const first = next[0] ?? createReservedSettingsImageOptionButton()
  next[0] = {
    ...createReservedSettingsImageOptionButton(),
    ...first,
    Name: RESERVED_IMAGE_OPTION_NAME,
  }
  return next
}

function clampButtonGroupIndices(group: SettingsButtonGroupDraft | null, length: number): void {
  group?.Buttons.forEach((item) => {
    if (item.index >= length) {
      item.index = 0
      item.enabled = false
    }
  })
}

function clampIndexedPosition(item: SettingsIndexedPosition, length: number): void {
  if (item.Index >= length) {
    item.Index = 0
  }
}

/**
 * 管理设置弹窗里的图片选项草稿、选择态与跨页签索引联动。
 * @description 统一处理按钮选项列表缩放、预览 URL 解析、保留位约束和多页签索引校正，避免组装层堆积重复状态。
 */
export function useSettingsImageOptions(params: UseSettingsImageOptionsParams) {
  const imageOptionDraft = ref<SettingsImageOptionDraft[]>([])
  const imageOptionSelectedIndex = ref(0)
  const imageOptionHover = ref(false)

  const imageOptionButtons: ComputedRef<SettingsImageOptionDraft[]> = computed(() => imageOptionDraft.value)

  const formatImageOptionLabel = (index: number, item: SettingsImageOptionDraft): string => {
    const order = String(index + 1).padStart(3, '0')
    if (index === 0) return `${order}：${RESERVED_IMAGE_OPTION_NAME}`
    return `${order}：${item.Name.trim() || '未命名按钮'}`
  }

  const selectedImageOption: ComputedRef<SettingsImageOptionDraft | null> = computed(() => {
    const index = imageOptionSelectedIndex.value
    if (index < 0) return null
    return imageOptionButtons.value[index] ?? null
  })

  const resolveImageOptionPreviewUrl = (index: number): string => {
    const imageOption = imageOptionButtons.value[index]
    if (!imageOption) return ''
    return (
      params.resolveGraphicPreviewUrl(imageOption.NoneImage.name ?? '', params.imageOptionGraphicBaseFolderPath)
      || params.resolveGraphicPreviewUrl(imageOption.MoveImage.name ?? '', params.imageOptionGraphicBaseFolderPath)
    )
  }

  const imageOptionPickerItems: ComputedRef<SettingsImageOptionPickerItem[]> = computed(() =>
    imageOptionButtons.value.map((item, index) => ({
      index,
      label: formatImageOptionLabel(index, item),
      name: item.Name,
      noneImagePath: item.NoneImage.name,
      moveImagePath: item.MoveImage.name,
    })),
  )

  const isReservedImageOption: ComputedRef<boolean> = computed(() => imageOptionSelectedIndex.value === 0)
  const selectedImageOptionNoneUrl: ComputedRef<string> = computed(() =>
    params.resolveGraphicPreviewUrl(
      selectedImageOption.value?.NoneImage.name ?? '',
      params.imageOptionGraphicBaseFolderPath,
    ),
  )
  const selectedImageOptionMoveUrl: ComputedRef<string> = computed(() =>
    params.resolveGraphicPreviewUrl(
      selectedImageOption.value?.MoveImage.name ?? '',
      params.imageOptionGraphicBaseFolderPath,
    ),
  )
  const imageOptionTestUrl: ComputedRef<string> = computed(() => {
    if (imageOptionHover.value && selectedImageOptionMoveUrl.value) return selectedImageOptionMoveUrl.value
    return selectedImageOptionNoneUrl.value || selectedImageOptionMoveUrl.value
  })
  const showImageOptionButtonTest: ComputedRef<boolean> = computed(() => {
    if (isReservedImageOption.value) return false
    return Boolean(selectedImageOptionNoneUrl.value || selectedImageOptionMoveUrl.value)
  })

  const syncSelectedIndexWithinBounds = (): void => {
    imageOptionSelectedIndex.value = Math.min(
      Math.max(imageOptionSelectedIndex.value, 0),
      Math.max(0, imageOptionDraft.value.length - 1),
    )
    if (imageOptionSelectedIndex.value === 0 && imageOptionDraft.value.length > 1) {
      imageOptionSelectedIndex.value = 1
    }
  }

  const resizeImageOptionList = (nextValue: number): void => {
    const size = Math.max(1, Math.floor(Number(nextValue) || 0))
    const current = imageOptionDraft.value.slice(0, size)
    while (current.length < size) {
      current.push(createEmptyImageOptionButton())
    }
    imageOptionDraft.value = normalizeImageOptionButtons(current)
    clampButtonGroupIndices(params.titleSetting.value, current.length)
    clampButtonGroupIndices(params.menuSetting.value, current.length)
    clampButtonGroupIndices(params.systemSetting.value, current.length)
    if (params.replaySetting.value && params.replaySetting.value.ButtonIndex >= current.length) {
      if (params.applyReplayButtonIndex) {
        params.applyReplayButtonIndex(0)
      } else {
        params.replaySetting.value.ButtonIndex = 0
        params.replaySetting.value.CloseEx.Index = 0
      }
    }
    if (params.saveLoadSetting.value) {
      clampIndexedPosition(params.saveLoadSetting.value.BackButtonEx, current.length)
      clampIndexedPosition(params.saveLoadSetting.value.CloseButtonEx, current.length)
      clampIndexedPosition(params.saveLoadSetting.value.PrePic, current.length)
      clampIndexedPosition(params.saveLoadSetting.value.NextPic, current.length)
    }
    syncSelectedIndexWithinBounds()
    imageOptionHover.value = false
  }

  const imageOptionMaxCount: WritableComputedRef<number> = computed({
    get: () => imageOptionButtons.value.length,
    set: (value: number) => resizeImageOptionList(value),
  })

  const setImageOptionButtons = (buttons: SettingsImageOptionDraft[]): void => {
    imageOptionDraft.value = normalizeImageOptionButtons(buttons)
    imageOptionSelectedIndex.value = imageOptionDraft.value.length > 1 ? 1 : 0
    imageOptionHover.value = false
  }

  const resetImageOptions = (): void => {
    imageOptionDraft.value = []
    imageOptionSelectedIndex.value = 0
    imageOptionHover.value = false
  }

  const selectImageOption = (index: number): void => {
    imageOptionSelectedIndex.value = index
    imageOptionHover.value = false
  }

  const updateSelectedImageOptionNonePic = (value: string): void => {
    if (imageOptionSelectedIndex.value === 0) return
    if (!selectedImageOption.value) return
    selectedImageOption.value.NoneImage.name = value
  }

  const updateSelectedImageOptionMovePic = (value: string): void => {
    if (imageOptionSelectedIndex.value === 0) return
    if (!selectedImageOption.value) return
    selectedImageOption.value.MoveImage.name = value
  }

  return {
    imageOptionDraft,
    imageOptionButtons,
    imageOptionMaxCount,
    imageOptionSelectedIndex,
    imageOptionHover,
    imageOptionPickerItems,
    isReservedImageOption,
    selectedImageOption,
    selectedImageOptionNoneUrl,
    selectedImageOptionMoveUrl,
    imageOptionTestUrl,
    showImageOptionButtonTest,
    setImageOptionButtons,
    resetImageOptions,
    selectImageOption,
    formatImageOptionLabel,
    updateSelectedImageOptionNonePic,
    updateSelectedImageOptionMovePic,
    resolveImageOptionPreviewUrl,
  } satisfies UseSettingsImageOptionsResult
}
